import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Iter 235.g (Carlos): Wrapper de scroll horizontal mejorado para tablas anchas.
 *
 * Problema resuelto:
 * - El scrollbar nativo aparece SOLO al fondo de la tabla → si tiene muchas filas,
 *   hay que scrollear todo abajo para acceder a él. Frustrante.
 *
 * Solución:
 * - Scrollbar SUPERIOR sticky, sincronizado con el inferior nativo.
 * - Soporte `shift+wheel` para scroll horizontal con la rueda.
 * - Sombras laterales (fade) indicando contenido oculto a izq/der.
 * - Flechas opcionales para scroll programático (botones ◀ ▶).
 * - Listo para usar como reemplazo directo de `<div className="overflow-x-auto">`.
 *
 * Props:
 *   className: extra clases al contenedor
 *   showArrows: mostrar botones ◀ ▶ (default true en desktop)
 *   topScrollbar: mostrar scrollbar superior (default true)
 *   children: contenido (típicamente <table>)
 */
export function StickyHScroll({
  className = '',
  showArrows = true,
  topScrollbar = true,
  children,
  ...rest
}) {
  const topRef = useRef(null);     // scrollbar superior (decorativo)
  const bottomRef = useRef(null);  // contenedor real con la tabla
  const innerRef = useRef(null);   // dummy interno del topRef (para ancho)
  const syncingRef = useRef(false);

  const [shadowL, setShadowL] = useState(false);
  const [shadowR, setShadowR] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  // Sincroniza el ancho del dummy interno con el ancho del contenido real
  const syncWidth = useCallback(() => {
    if (!bottomRef.current || !innerRef.current) return;
    const inner = bottomRef.current.firstElementChild;
    if (inner) {
      innerRef.current.style.width = `${inner.scrollWidth}px`;
    }
    const el = bottomRef.current;
    setHasOverflow(el.scrollWidth > el.clientWidth + 4);
  }, []);

  // Actualiza sombras según posición de scroll
  const updateShadows = useCallback(() => {
    const el = bottomRef.current;
    if (!el) return;
    setShadowL(el.scrollLeft > 2);
    setShadowR(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  // Sincroniza ambos scrollbars
  const handleScroll = useCallback((src) => () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (src === 'top' && bottomRef.current && topRef.current) {
      bottomRef.current.scrollLeft = topRef.current.scrollLeft;
    } else if (src === 'bottom' && bottomRef.current && topRef.current) {
      topRef.current.scrollLeft = bottomRef.current.scrollLeft;
    }
    updateShadows();
    syncingRef.current = false;
  }, [updateShadows]);

  // Shift+wheel para scroll horizontal
  const handleWheel = useCallback((e) => {
    if (!bottomRef.current) return;
    // Si scroll vertical y shift NO presionado, dejar comportamiento normal
    if (!e.shiftKey && Math.abs(e.deltaY) > Math.abs(e.deltaX)) return;
    e.preventDefault();
    bottomRef.current.scrollLeft += (e.deltaY !== 0 ? e.deltaY : e.deltaX);
  }, []);

  useEffect(() => {
    syncWidth();
    updateShadows();
    const ro = new ResizeObserver(() => {
      syncWidth();
      updateShadows();
    });
    if (bottomRef.current?.firstElementChild) {
      ro.observe(bottomRef.current.firstElementChild);
    }
    if (bottomRef.current) ro.observe(bottomRef.current);
    return () => ro.disconnect();
  }, [syncWidth, updateShadows, children]);

  const scrollBy = useCallback((delta) => {
    if (!bottomRef.current) return;
    bottomRef.current.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  return (
    <div className={`relative ${className}`} {...rest}>
      {/* Sombras laterales */}
      {shadowL && (
        <div className="pointer-events-none absolute top-0 bottom-0 left-0 w-6 z-10 bg-gradient-to-r from-black/10 to-transparent" />
      )}
      {shadowR && (
        <div className="pointer-events-none absolute top-0 bottom-0 right-0 w-6 z-10 bg-gradient-to-l from-black/10 to-transparent" />
      )}

      {/* Scrollbar superior */}
      {topScrollbar && hasOverflow && (
        <div
          ref={topRef}
          onScroll={handleScroll('top')}
          className="overflow-x-auto overflow-y-hidden sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-slate-200"
          style={{ height: 14 }}
          aria-hidden="true"
          data-testid="sticky-hscroll-top"
        >
          <div ref={innerRef} style={{ height: 1 }} />
        </div>
      )}

      {/* Botones de scroll */}
      {showArrows && hasOverflow && (
        <>
          {shadowL && (
            <button
              type="button"
              onClick={() => scrollBy(-300)}
              className="absolute left-1 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-white shadow-md border border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 flex items-center justify-center text-slate-600 hover:text-indigo-700 transition"
              aria-label="Scroll izquierda"
              data-testid="sticky-hscroll-left"
              title="Scroll izquierda (Shift+rueda también)"
            >
              ◀
            </button>
          )}
          {shadowR && (
            <button
              type="button"
              onClick={() => scrollBy(300)}
              className="absolute right-1 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-white shadow-md border border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 flex items-center justify-center text-slate-600 hover:text-indigo-700 transition"
              aria-label="Scroll derecha"
              data-testid="sticky-hscroll-right"
              title="Scroll derecha (Shift+rueda también)"
            >
              ▶
            </button>
          )}
        </>
      )}

      {/* Contenedor real */}
      <div
        ref={bottomRef}
        onScroll={handleScroll('bottom')}
        onWheel={handleWheel}
        className="overflow-x-auto"
        data-testid="sticky-hscroll-content"
      >
        {children}
      </div>
    </div>
  );
}

export default StickyHScroll;
